import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { Grid, GridColumn as Column, GridDataStateChangeEvent, GridToolbar, GridCustomCellProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { paths } from '../../../routes';
import { fetchApi } from '../../../services/api';
import { Title } from '../../../utils/title';
import { plusIcon, arrowRotateCwIcon } from '@progress/kendo-svg-icons';
import CreateWorkOrderDialog from './CreateWorkOrderDialog';
import CreateCounterSaleDialog from './CreateCounterSaleDialog';
import { WorkOrderStatus, WorkOrderPriority, formatPriorityLabel, formatStatusLabel, getStatusBadgeStyle, SHOP_GROUP_ID } from './WorkOrderDetail';
import OwnerAutoComplete, { OwnerAutoCompleteViewModel } from '../../AssetOwners/OwnerAutoComplete';
import VehicleAutoComplete from '../../AssetVehicles/VehicleAutoComplete';
import TrailerAutoComplete, { TrailerAutoCompleteViewModel } from '../../AssetTrailers/TrailerAutoComplete';
import { Label } from '@progress/kendo-react-labels';
import { DropdownFilterCellIDName } from 'components/cells/DropdownFilterCellIDName';

interface WorkOrderSummaryViewModel {
  WorkOrderID: number;
  OwnerID: number;
  VehicleID?: number;
  TrailerID?: number;
  Status: WorkOrderStatus;
  Priority: WorkOrderPriority;
  APUHours?: number;
  ChassisMiles?: number;
  ScheduledStartDate?: string;
  ActualStartDate?: string;
  CompletedDate?: string;
  InvoicedDate?: string;
  PaidDate?: string;
  ActualTotalCost: number;
  Description?: string;
  CreatedDateTime: string;
  TaskCount: number;
  CompletedTaskCount: number;
  OwnerName: string;
  VehicleNumber: string;
  TrailerNumber: string;
}

interface WorkOrdersResponse {
  Items: {
    Data: WorkOrderSummaryViewModel[];
    Total: number;
  };
}

type ApplicationUser = {
  ID: string;
  Name: string;
};

type UsersDropdownResponse = {
  Users: ApplicationUser[];
};

const ShopWorkOrders: React.FC = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [gridState, setGridState] = useState<GridState>({
    skip: 0,
    take: 25,
    sort: [{ field: 'CreatedDateTime', dir: 'desc' }]
  });
  const [data, setData] = useState<WorkOrderSummaryViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [ownerFilter, setOwnerFilter] = useState<number | null>(null);
  const [vehicleFilter, setVehicleFilter] = useState<number | null>(null);
  const [trailerFilter, setTrailerFilter] = useState<number | null>(null);
  const [assignedTechFilter, setAssignedTechFilter] = useState<string>('');
  const [assignedTechOptions, setAssignedTechOptions] = useState<ApplicationUser[]>([]);
  const [loadingAssignedTechs, setLoadingAssignedTechs] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showCounterSaleDialog, setShowCounterSaleDialog] = useState(false);

  const statusOptions = [
    { text: 'All Statuses', value: '' },
    { text: 'Open', value: WorkOrderStatus.Open },
    { text: 'On Hold', value: WorkOrderStatus.OnHold },
    { text: 'In Progress', value: WorkOrderStatus.InProgress },
    { text: 'Vendor', value: WorkOrderStatus.Vendor },
    { text: 'Completed', value: WorkOrderStatus.Completed },
    { text: 'Invoiced', value: WorkOrderStatus.Invoiced },
    { text: 'Paid', value: WorkOrderStatus.Paid },
    { text: 'Cancelled', value: WorkOrderStatus.Cancelled },
  ];

  const statusColumnFilterOptions = [
    { ID: WorkOrderStatus.Open, Name: 'Open' },
    { ID: WorkOrderStatus.OnHold, Name: 'On Hold' },
    { ID: WorkOrderStatus.InProgress, Name: 'In Progress' },
    { ID: WorkOrderStatus.Vendor, Name: 'Vendor' },
    { ID: WorkOrderStatus.Completed, Name: 'Completed' },
    { ID: WorkOrderStatus.Invoiced, Name: 'Invoiced' },
    { ID: WorkOrderStatus.Paid, Name: 'Paid' },
    { ID: WorkOrderStatus.Cancelled, Name: 'Cancelled' },
  ];

  const priorityOptions = [
    { text: 'All Priorities', value: '' },
    { text: 'Low', value: WorkOrderPriority.Low },
    { text: 'Normal', value: WorkOrderPriority.Normal },
    { text: 'High', value: WorkOrderPriority.High },
    { text: 'Critical', value: WorkOrderPriority.Critical },
  ];

  const priorityColumnFilterOptions = [
    { ID: WorkOrderPriority.Low, Name: 'Low' },
    { ID: WorkOrderPriority.Normal, Name: 'Normal' },
    { ID: WorkOrderPriority.High, Name: 'High' },
    { ID: WorkOrderPriority.Critical, Name: 'Critical' },
  ];

  const loadData = useCallback(async (state: GridState) => {
    setLoading(true);
    try {
      const payload = {
        Status: statusFilter,
        Priority: priorityFilter,
        OwnerID: ownerFilter,
        VehicleID: vehicleFilter,
        TrailerID: trailerFilter,
        AssignedTo: assignedTechFilter || null
      }

      const response: WorkOrdersResponse = await fetchApi(`/api/ShopWorkOrder/WorkOrders?${toDataSourceRequestString(state)}`, payload, 'POST');
      setData(response.Items.Data);
      setTotal(response.Items.Total);
    } catch (error) {
      console.error('Error loading work orders:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, ownerFilter, vehicleFilter, trailerFilter, assignedTechFilter]);

  useEffect(() => {
    setLoadingAssignedTechs(true);
    fetchApi(`/api/User/UsersDropdown?groupId=${SHOP_GROUP_ID}`)
      .then((response: UsersDropdownResponse) => {
        setAssignedTechOptions(response.Users);
      })
      .catch((error) => {
        console.error('Error loading assigned technician options:', error);
      })
      .finally(() => setLoadingAssignedTechs(false));
  }, []);

  useEffect(() => {
    loadData(gridState);
  }, [gridState, loadData]);

  const resetPaging = useCallback(() => {
    setGridState((prev) => ({ ...prev, skip: 0 }));
  }, []);

  const onGridStateChange = (event: GridDataStateChangeEvent) => {
    setGridState(event.dataState);
  };

  const viewWorkOrderDetail = (workOrderId: number) => {
    history.push(paths.ShopWorkOrderDetail.replace(':workOrderId', workOrderId.toString()));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadgeColor = (status: WorkOrderStatus) => {
    switch (status) {
      case WorkOrderStatus.Open: return 'info';
      case WorkOrderStatus.OnHold: return 'secondary';
      case WorkOrderStatus.InProgress: return 'warning';
      case WorkOrderStatus.Completed: return 'success';
      case WorkOrderStatus.Invoiced: return 'primary';
      case WorkOrderStatus.Paid: return 'success';
      case WorkOrderStatus.Cancelled: return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeColor = (priority: WorkOrderPriority) => {
    switch (priority) {
      case WorkOrderPriority.Low: return 'secondary';
      case WorkOrderPriority.Normal: return 'info';
      case WorkOrderPriority.High: return 'warning';
      case WorkOrderPriority.Critical: return 'danger';
      default: return 'secondary';
    }
  };

  const WorkOrderNumberCell = (props: GridCustomCellProps) => {
    const { dataItem } = props;
    return (
      <td {...props.tdProps}>
        <Button
          fillMode="flat"
          themeColor="primary"
          size="small"
          onClick={() => viewWorkOrderDetail(dataItem.WorkOrderID)}
        >
          {dataItem.WorkOrderID}
        </Button>
      </td>
    );
  };

  const StatusCell = (props: GridCustomCellProps) => {
    const { dataItem } = props;
    return (
      <td {...props.tdProps}>
        <span className={`badge badge-${getStatusBadgeColor(dataItem.Status)}`} style={getStatusBadgeStyle(dataItem.Status)}>
          {formatStatusLabel(dataItem.Status)}
        </span>
      </td>
    );
  };

  const PriorityCell = (props: GridCustomCellProps) => {
    const { dataItem } = props;
    return (
      <td {...props.tdProps}>
        <span className={`badge badge-${getPriorityBadgeColor(dataItem.Priority)}`}>
          {formatPriorityLabel(dataItem.Priority)}
        </span>
      </td>
    );
  };

  const ProgressCell = (props: GridCustomCellProps) => {
    const { dataItem } = props;
    return <td {...props.tdProps}>
      {dataItem.CompletedTaskCount} / {dataItem.TaskCount}
    </td>;
  };

  const VehicleCell = (props: GridCustomCellProps) => {
    const dataItem = props.dataItem as WorkOrderSummaryViewModel;

    return (
      <td {...props.tdProps}>
        {dataItem.VehicleID
          ? <Link style={{ color: '#007bff' }} to={`/Assets/Vehicle/${dataItem.VehicleID}`}>{dataItem.VehicleNumber}</Link>
          : dataItem.VehicleNumber}
      </td>
    );
  };

  const TrailerCell = (props: GridCustomCellProps) => {
    const dataItem = props.dataItem as WorkOrderSummaryViewModel;

    return (
      <td {...props.tdProps}>
        {dataItem.TrailerID
          ? <Link style={{ color: '#007bff' }} to={`/Assets/Trailer/${dataItem.TrailerID}`}>{dataItem.TrailerNumber}</Link>
          : dataItem.TrailerNumber}
      </td>
    );
  };

  return <>
    <Title string="Shop Work Orders" />
    {showCreateDialog && (
      <CreateWorkOrderDialog
        onClose={() => setShowCreateDialog(false)}
        onCreated={async (workOrderId) => {
          setShowCreateDialog(false);

          if (workOrderId) {
            viewWorkOrderDetail(workOrderId);
            return;
          }

          const nextState = { ...gridState, skip: 0 };
          setGridState(nextState);
          await loadData(nextState);
        }}
      />
    )}
    {showCounterSaleDialog && (
      <CreateCounterSaleDialog
        onClose={() => setShowCounterSaleDialog(false)}
        onCreated={(workOrderId) => {
          setShowCounterSaleDialog(false);
          if (workOrderId) {
            viewWorkOrderDetail(workOrderId);
          }
        }}
      />
    )}
    <br />
    {/* Filters */}
    <div className="row mb-3">
      <div className="col-md-4">
        <Label>Status Filter:</Label>
        <DropDownList
          data={statusOptions}
          textField="text"
          dataItemKey="value"
          value={statusOptions.find(opt => opt.value === statusFilter)}
          onChange={(e) => {
            setStatusFilter(e.value.value);
            resetPaging();
          }}
        />
      </div>
      <div className="col-md-4">
        <Label>Priority Filter:</Label>
        <DropDownList
          data={priorityOptions}
          textField="text"
          dataItemKey="value"
          value={priorityOptions.find(opt => opt.value === priorityFilter)}
          onChange={(e) => {
            setPriorityFilter(e.value.value);
            resetPaging();
          }}
        />
      </div>
      <div className="col-md-4">
        <Label>Assigned Technician:</Label>
        <DropDownList
          data={assignedTechOptions}
          textField="Name"
          dataItemKey="ID"
          defaultItem={{ ID: '', Name: 'All Technicians' }}
          value={assignedTechFilter ? assignedTechOptions.find(opt => opt.ID === assignedTechFilter) ?? null : null}
          onChange={(e) => {
            const id = e.value?.ID as string | undefined;
            setAssignedTechFilter(id && id.length ? id : '');
            resetPaging();
          }}
          disabled={loadingAssignedTechs}
          style={{ width: '100%' }}
        />
      </div>
    </div>

    <div className="row mb-3">
      <div className="col-md-4">
        <Label>Owner:</Label>
        <OwnerAutoComplete
          style={{ width: '100%' }}
          placeholder="Select Owner"
          selectedOwnerID={ownerFilter ?? undefined}
          onSelectedOwner={(owner) => {
            const ownerId = (owner as OwnerAutoCompleteViewModel | null)?.OwnerID ?? null;
            setOwnerFilter(ownerId);
            resetPaging();
          }}
        />
      </div>
      <div className="col-md-4">
        <Label>Vehicle:</Label>
        <VehicleAutoComplete
          style={{ width: '100%' }}
          placeholder="Select Vehicle"
          selectedVehicleID={vehicleFilter ?? undefined}
          onSelectedVehicle={(vehicle) => {
            const vehicleId = vehicle?.VehicleID ?? null;
            setVehicleFilter(vehicleId);
            resetPaging();
          }}
        />
      </div>
      <div className="col-md-4">
        <Label>Trailer:</Label>
        <TrailerAutoComplete
          style={{ width: '100%' }}
          placeholder="Select Trailer"
          selectedTrailerID={trailerFilter ?? undefined}
          onSelectedTrailer={(trailer) => {
            const trailerId = (trailer as TrailerAutoCompleteViewModel | null)?.TrailerID ?? null;
            setTrailerFilter(trailerId);
            resetPaging();
          }}
        />
      </div>
    </div>

    <Grid
      data={data}
      showLoader={loading}
      total={total}
      pageable={true}
      sortable={true}
      filterable={true}
      {...gridState}
      onDataStateChange={onGridStateChange}
      scrollable='none'
    >
      <GridToolbar>
        <Button svgIcon={plusIcon} themeColor="success" onClick={() => setShowCreateDialog(true)}>
          Work Order
        </Button>
        <Button svgIcon={plusIcon} themeColor="primary" onClick={() => setShowCounterSaleDialog(true)}>
          Counter Sale
        </Button>
        <Button
          svgIcon={arrowRotateCwIcon}
          fillMode="flat"
          title="Refresh"
          onClick={() => loadData(gridState)}
        />
      </GridToolbar>
      <Column field="WorkOrderID" title="Work Order #" cells={{ data: WorkOrderNumberCell }} filter="numeric" />
      <Column field="OwnerName" title="Customer" filterable={false} />
      <Column field="VehicleNumber" title="Vehicle" cells={{ data: VehicleCell }} filterable={false} />
      <Column field="TrailerNumber" title="Trailer" cells={{ data: TrailerCell }} filterable={false} />
      <Column
        field="Status"
        title="Status"
        filter="numeric"
        cells={{
          data: StatusCell,
          filterCell: (props) => <DropdownFilterCellIDName {...props} data={statusColumnFilterOptions} />
        }}
      />
      <Column
        field="Priority"
        title="Priority"
        filter="numeric"
        cells={{
          data: PriorityCell,
          filterCell: (props) => <DropdownFilterCellIDName {...props} data={priorityColumnFilterOptions} />
        }}
      />
      <Column field="Description" title="Description" />
      <Column field="ScheduledStartDate" title="Scheduled Start" cells={{ data: (props) => <td {...props.tdProps}>{formatDate(props.dataItem.ScheduledStartDate)}</td> }} filter="date" />
      <Column field="ActualTotalCost" title="Actual Cost" format="{0:c}" filter="numeric" />
      <Column title="Progress" cells={{ data: ProgressCell }} filterable={false} />
      <Column field="CreatedDateTime" title="Written" cells={{ data: (props) => <td {...props.tdProps}>{formatDate(props.dataItem.CreatedDateTime)}</td> }} filter="date" />
    </Grid>
  </>
};

export default ShopWorkOrders;
